import { CalculateByParamRequest, CalculateByParamRequestParamEnum } from './AnalyticPanelModels';

export interface SavedInvestmentFilter {
    id: number;
    name: string;
}

export type { CalculateByParamRequest };
export { CalculateByParamRequestParamEnum as CalculateParamEnum };

export interface AnalyticPanelCosts {
    notary: string | null;
    pcc: string | null;
    brokerPercent: string | null;
    rentFees: string | null;
    otherFees: string | null;
    maintenanceFees: string | null;
    otherMaintenanceCosts: string | null;
    portalFees: string | null;
    photographerAmount: string | null;
    renovation: string | null;
    equipment: string | null;
    homeStaging: string | null;
    saleBrokerPercent: string | null;
    otherSaleCosts: string | null;
    otherSaleFees: string | null;
    mediaCosts: string | null;
    operatingCosts: string | null;
    otherPurchaseCosts: string | null;
    otherCosts: string | null;
    saleInterest: string | null;
    sumOfCosts: string | null;
}

export interface AnalyticPanelState {
    filterId: number | null;
    filterName: string;
    savedFilters: SavedInvestmentFilter[];
    purchasePrice: string;
    salePrice: string | null;
    area: string | null;
    purchaseDate: string | null;
    saleDate: string | null;
    investmentTime: string | null;
    address: string | null;
    floor: string | null;
    rooms: string | null;
    filter: AnalyticPanelFilter | null;
    calculation: AnalyticPanelCalculation | null;
    flip: AnalyticPanelFlip | null;
    costs: AnalyticPanelCosts | null;
    isLoading: boolean;
}

export interface InvestmentObject {
    id: number | null;
    name: string | null;
    price: string | null;
    salePrice: string | null;
    purchaseDate: string | null;
    saleDate: string | null;
    address: string | null;
    investmentTime: string | null;
    area: string | null;
    rooms: string | null;
    floor: string | null;
    filter: AnalyticPanelFilter;
    calculation: AnalyticPanelCalculation;
    flip: AnalyticPanelFlip;
    costs: AnalyticPanelCosts;
}

export interface AnalyticPanelFilter {
    notary: string | null;
    pcc: string | null;
    brokerPercent: string | null;
    rentFees: string | null;
    otherFees: string | null;
    maintenanceFees: string | null;
    portalFees: string | null;
    photographerAmount: string | null;
    renovation: string | null;
    equipment: string | null;
    homeStaging: string | null;
    saleBrokerPercent: string | null;
    otherSaleCosts: string | null;
    mediaCosts: string | null;
    operatingCosts: string | null;
    otherPurchaseCosts: string | null;
    otherCosts: string | null;
    saleInterest: string | null;
}

export interface AnalyticPanelCalculation {
    flipperProfitAmount: string | null;
    flipperProfitPercent: string | null;
    investorProfitAmount: string | null;
    investorProfitPercent: string | null;
    flipperCapitalAmount: string | null;
    flipperCapitalPercent: string | null;
    investorCapitalAmount: string | null;
    investorCapitalPercent: string | null;
    projectCapital: string | null;
    salePrice?: string | null;
    roi?: string | null;
    flipperRoe?: string | null;
    investorRoe?: string | null;
    margin?: string | null;
    estimatedProfitNetto?: string | null;
}

export interface AnalyticPanelFlip {
    firstStage: string | null;
    secondStage: string | null;
    thirdStage: string | null;
    finalCosts: string | null;
    unusedBudget: string | null;
}

export type CalculateInvestmentApiResponse = InvestmentObject;
export type CalculateInvestmentRequest = InvestmentObject;
export type SaveInvestmentFilterRequest = InvestmentObject;

export interface CalculateByParamResponse {
    value: number;
    flipperCapitalAmount?: string;
    flipperCapitalPercent?: string;
    investorCapitalAmount?: string;
    investorCapitalPercent?: string;
    flipperProfitAmount?: string;
    flipperProfitPercent?: string;
    investorProfitAmount?: string;
    investorProfitPercent?: string;
}

export const FIELD_TO_PARAM_ENUM: Partial<Record<keyof AnalyticPanelFilter, CalculateByParamRequestParamEnum>> = {
    maintenanceFees: CalculateByParamRequestParamEnum.MAINTENANCE_FEES,
    rentFees: CalculateByParamRequestParamEnum.RENT_FEES,
    brokerPercent: CalculateByParamRequestParamEnum.BROKER,
    saleBrokerPercent: CalculateByParamRequestParamEnum.SALE_BROKER,
    renovation: CalculateByParamRequestParamEnum.RENOVATION,
    pcc: CalculateByParamRequestParamEnum.PCC,
    saleInterest: 'SALE_INTEREST' as CalculateByParamRequestParamEnum
};

export const CALCULATION_FIELD_TO_PARAM_ENUM: Partial<Record<keyof AnalyticPanelCalculation, CalculateByParamRequestParamEnum>> = {
    flipperCapitalPercent: CalculateByParamRequestParamEnum.FLIPPER_CAPITAL,
    investorCapitalPercent: CalculateByParamRequestParamEnum.INVESTOR_CAPITAL,
    flipperProfitPercent: CalculateByParamRequestParamEnum.FLIPPER_PROFIT,
    investorProfitPercent: CalculateByParamRequestParamEnum.INVESTOR_PROFIT
};

export const CALCULATION_PERCENT_PAIRS: Partial<Record<keyof AnalyticPanelCalculation, keyof AnalyticPanelCalculation>> = {
    flipperCapitalPercent: 'investorCapitalPercent',
    investorCapitalPercent: 'flipperCapitalPercent',
    flipperProfitPercent: 'investorProfitPercent',
    investorProfitPercent: 'flipperProfitPercent'
};

export const CALCULATION_FIELD_TO_RESPONSE_KEY: Partial<Record<keyof AnalyticPanelCalculation, keyof CalculateByParamResponse>> = {
    flipperCapitalPercent: 'flipperCapitalPercent',
    investorCapitalPercent: 'investorCapitalPercent',
    flipperProfitPercent: 'flipperProfitPercent',
    investorProfitPercent: 'investorProfitPercent'
};

export const calculateEndDate = (startDate: string, flipTimeMonths: number): string => {
    if (!startDate || !flipTimeMonths) return '';

    const date = new Date(startDate);
    date.setMonth(date.getMonth() + Math.ceil(flipTimeMonths));

    return date.toISOString().split('T')[0];
};

export const calculateDurationMonths = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);

    const months = (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth()) +
        (end.getDate() - start.getDate()) / 30;

    return Math.round(months * 10) / 10;
};

export const getTodayDate = (): string => {
    return new Date().toISOString().split('T')[0];
};

export const initialAnalyticPanelState: AnalyticPanelState = {
    filterId: null,
    filterName: '',
    savedFilters: [],
    purchasePrice: '',
    salePrice: null,
    area: null,
    purchaseDate: null,
    saleDate: null,
    investmentTime: null,
    address: '',
    floor: null,
    rooms: null,
    filter: null,
    calculation: null,
    flip: null,
    costs: null,
    isLoading: false
};

